import { useCallback, useEffect, useRef, useState } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import type { WsConnectionStatus, WsOutgoingMessage, WsIncomingMessage } from '../types';

interface UseWsConnectionOptions {
  url: string;
  reconnectInterval?: number;
  /** 0 means unlimited reconnect attempts. */
  maxReconnectAttempts?: number;
  heartbeatInterval?: number;
  onMessage?: (message: WsIncomingMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Error | Event) => void;
}

interface UseWsConnectionReturn {
  sendMessage: (message: WsOutgoingMessage) => void;
  lastMessage: WsIncomingMessage | null;
  readyState: ReadyState;
  connectionStatus: WsConnectionStatus;
  connect: () => void;
  disconnect: () => void;
  reconnect: () => void;
  wsLatencyMs: number | null;
  lastPongAt: Date | null;
  missedHeartbeats: number;
  reconnectCount: number;
}

const DEFAULT_OPTIONS = {
  reconnectInterval: 3000,
  maxReconnectAttempts: 0,
  heartbeatInterval: 30000,
};

export function useWsConnection(options: UseWsConnectionOptions): UseWsConnectionReturn {
  const {
    url,
    reconnectInterval = DEFAULT_OPTIONS.reconnectInterval,
    maxReconnectAttempts = DEFAULT_OPTIONS.maxReconnectAttempts,
    heartbeatInterval = DEFAULT_OPTIONS.heartbeatInterval,
    onMessage,
    onConnect,
    onDisconnect,
    onError,
  } = options;

  const [connectionStatus, setConnectionStatus] = useState<WsConnectionStatus>(() =>
    url ? 'connecting' : 'disconnected'
  );
  const [lastMessage, setLastMessage] = useState<WsIncomingMessage | null>(null);
  const [shouldConnect, setShouldConnect] = useState(() => url !== '');
  const [wsLatencyMs, setWsLatencyMs] = useState<number | null>(null);
  const [lastPongAt, setLastPongAt] = useState<Date | null>(null);
  const [missedHeartbeats, setMissedHeartbeats] = useState(0);
  const [reconnectCount, setReconnectCount] = useState(0);

  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isManualDisconnectRef = useRef(false);
  const lastPingTimestampRef = useRef<number | null>(null);
  const lastAcknowledgedPingRef = useRef<number | null>(null);

  useEffect(() => {
    if (url) setShouldConnect(true);
  }, [url]);

  const { sendJsonMessage, readyState } = useWebSocket<WsIncomingMessage>(
    url,
    {
      share: true,
      retryOnError: true,
      shouldReconnect: () => {
        if (isManualDisconnectRef.current) return false;

        const hasLimit = Number.isFinite(maxReconnectAttempts) && maxReconnectAttempts > 0;
        if (hasLimit && reconnectAttemptsRef.current >= maxReconnectAttempts) {
          setConnectionStatus('disconnected');
          onError?.(new Error('Max reconnection attempts reached'));
          return false;
        }

        reconnectAttemptsRef.current += 1;
        setReconnectCount((count) => count + 1);
        setConnectionStatus('reconnecting');
        return true;
      },
      reconnectInterval,
      // react-use-websocket also has its own attempt cap; keep it effectively
      // unlimited when the POS-level maxReconnectAttempts is 0.
      reconnectAttempts: maxReconnectAttempts > 0 ? maxReconnectAttempts : Number.MAX_SAFE_INTEGER,
      onOpen: () => {
        reconnectAttemptsRef.current = 0;
        setConnectionStatus('connected');
        setMissedHeartbeats(0);
        onConnect?.();
      },
      onClose: () => {
        setConnectionStatus(isManualDisconnectRef.current ? 'disconnected' : 'reconnecting');
        onDisconnect?.();
      },
      onError: (error) => {
        if (!isManualDisconnectRef.current) setConnectionStatus('reconnecting');
        onError?.(error);
      },
      onMessage: (event) => {
        try {
          const message = JSON.parse(event.data) as WsIncomingMessage;

          if (message.type === 'pong' && typeof message.timestamp === 'number') {
            const latency = Math.max(0, Date.now() - message.timestamp);
            lastAcknowledgedPingRef.current = message.timestamp;
            setWsLatencyMs(latency);
            setLastPongAt(new Date());
            setMissedHeartbeats(0);
          }

          setLastMessage(message);
          onMessage?.(message);
        } catch (err) {
          console.error('[WebSocket] Failed to parse message:', err);
        }
      },
    },
    shouldConnect && url !== ''
  );

  const sendMessage = useCallback(
    (message: WsOutgoingMessage) => {
      if (readyState === ReadyState.OPEN) {
        try {
          sendJsonMessage(message);
        } catch (err) {
          console.error('[WebSocket] Failed to send message:', err);
        }
      } else {
        console.warn('[WebSocket] Cannot send message, connection is not open');
      }
    },
    [readyState, sendJsonMessage]
  );

  const sendHeartbeat = useCallback(() => {
    if (readyState !== ReadyState.OPEN) return;

    const previousPing = lastPingTimestampRef.current;
    if (
      previousPing !== null &&
      (lastAcknowledgedPingRef.current === null || lastAcknowledgedPingRef.current < previousPing)
    ) {
      setMissedHeartbeats((count) => count + 1);
    }

    const timestamp = Date.now();
    lastPingTimestampRef.current = timestamp;
    sendMessage({ type: 'ping', timestamp });
  }, [readyState, sendMessage]);

  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      sendHeartbeat();
      heartbeatRef.current = setInterval(sendHeartbeat, heartbeatInterval);
    }

    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
  }, [readyState, heartbeatInterval, sendHeartbeat]);

  const connect = useCallback(() => {
    isManualDisconnectRef.current = false;
    reconnectAttemptsRef.current = 0;
    setShouldConnect(true);
    setConnectionStatus('connecting');
  }, []);

  const disconnect = useCallback(() => {
    isManualDisconnectRef.current = true;
    setShouldConnect(false);
    setConnectionStatus('disconnected');
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const reconnect = useCallback(() => {
    disconnect();
    setTimeout(() => {
      isManualDisconnectRef.current = false;
      reconnectAttemptsRef.current = 0;
      setShouldConnect(true);
      setConnectionStatus('connecting');
    }, 100);
  }, [disconnect]);

  return {
    sendMessage,
    lastMessage,
    readyState,
    connectionStatus,
    connect,
    disconnect,
    reconnect,
    wsLatencyMs,
    lastPongAt,
    missedHeartbeats,
    reconnectCount,
  };
}
